import type { LinkChapterDto, PaginatedListOfLinkChapterDto } from "../../models/models"
import type { PagingActions, PagingOptions } from "../../models/types"
import {
  getAllLinkChapters,
  getLinkChapter,
  postLinkChapter,
  putLinkChapter,
  removeLinkChapter
} from "../../services/LinkChapterService"
import { getQueryString } from "../../utils"
import type { AppState } from "../app.state"
import type { AppThunkAction, AppThunkDispatch } from "../app.types"
import { history } from "./../Store"
import type { LinkChapter, LinkChapterAction, LinkChapterListAction } from "./linkchapter.types"

export const LINKCHAPTER_CREATE: string = "LINKCHAPTER_CREATE"

export const LINKCHAPTER_FETCH_LIST: string = "LINKCHAPTER_FETCH_LIST"
export const LINKCHAPTER_FETCH: string = "LINKCHAPTER_FETCH"

export const LINKCHAPTER_UPDATE: string = "LINKCHAPTER_UPDATE"

export const LINKCHAPTER_DELETE: string = "LINKCHAPTER_DELETE"

export const LINKCHAPTER_LOAD_DEFAULT: string = "LINKCHAPTER_LOAD_DEFAULT"
export const LINKCHAPTER_LOAD_LIST: string = "LINKCHAPTER_LOAD_LIST"
export const LINKCHAPTER_LOAD: string = "LINKCHAPTER_LOAD"

export const LINKCHAPTER_CHANGE_PAGE: string = "LINKCHAPTER_CHANGE_PAGE"
export const LINKCHAPTER_LOAD_LIST_OPTIONS: string = "LINKCHAPTER_LOAD_LIST_OPTIONS"

export function createLinkChapter(chapter: LinkChapter): AppThunkAction<void, LinkChapterAction> {
  return async (dispatch: AppThunkDispatch<LinkChapterAction>) => {
    dispatch({ type: LINKCHAPTER_CREATE })
    const newChapterId: string = await postLinkChapter(chapter)
    const newChapter: LinkChapterDto = await getLinkChapter(newChapterId)

    history.replace(`/manage/linkchapters/${newChapterId}`)
    dispatch(loadLinkChapter(newChapter))
  }
}

export function fetchLinkChapterList(
  pageIndex = 0,
  pageSize = 50
): AppThunkAction<void, LinkChapterListAction | PagingActions> {
  return async (dispatch: AppThunkDispatch<LinkChapterListAction | PagingActions>, getState: () => AppState) => {
    dispatch({ type: LINKCHAPTER_FETCH_LIST })

    const state: AppState = getState()
    const queryString: string = getQueryString(pageIndex, pageSize, state.contentManager.sort)

    const pagedList: PaginatedListOfLinkChapterDto | undefined = await getAllLinkChapters(queryString)

    if (pagedList) {
      const pagingOptions: PagingOptions = {
        currentPage: pagedList.pageIndex,
        totalPages: pagedList.totalPages,
        pageSize: pageSize
      }

      dispatch(loadLinkChapterList(pagedList.items as LinkChapterDto[]))
      dispatch({
        type: LINKCHAPTER_LOAD_LIST_OPTIONS,
        payload: pagingOptions
      })
    }
  }
}

export function fetchLinkChapter(id: string): AppThunkAction<void, LinkChapterAction> {
  return async (dispatch: AppThunkDispatch<LinkChapterAction>) => {
    dispatch({ type: LINKCHAPTER_FETCH })

    const chapter: LinkChapterDto = await getLinkChapter(id)

    dispatch(loadLinkChapter(chapter))
  }
}

export function updateLinkChapter(chapter: LinkChapter): AppThunkAction<void, LinkChapterAction> {
  return async (dispatch: AppThunkDispatch<LinkChapterAction>) => {
    dispatch({ type: LINKCHAPTER_UPDATE })
    await putLinkChapter(chapter)
    const updatedChapter: LinkChapterDto = await getLinkChapter(chapter.id as string)
    dispatch(loadLinkChapter(updatedChapter))
  }
}

export function deleteLinkChapter(id: string): AppThunkAction<void, LinkChapterAction> {
  return async (dispatch: AppThunkDispatch<LinkChapterAction>) => {
    dispatch({ type: LINKCHAPTER_DELETE })
    await removeLinkChapter(id)
    dispatch(fetchLinkChapterList())
  }
}

export function changeLinkChapterPage(page: number): AppThunkAction<void, PagingActions> {
  return (dispatch: AppThunkDispatch<PagingActions>, getState: () => AppState) => {
    const state = getState()
    dispatch({ type: LINKCHAPTER_CHANGE_PAGE, payload: page })

    dispatch(fetchLinkChapterList(page, state.linkChapters.pagingOptions.pageSize))
  }
}

export function loadDefaultLinkChapter(): LinkChapterAction {
  return {
    type: LINKCHAPTER_LOAD_DEFAULT
  }
}

export function loadLinkChapterList(dtoList: LinkChapterDto[]): LinkChapterListAction {
  const chapterList: LinkChapter[] = dtoList.map(linkChapterDtoToLinkChapter)
  return {
    type: LINKCHAPTER_LOAD_LIST,
    payload: chapterList
  }
}

export function loadLinkChapter(dto: LinkChapterDto): LinkChapterAction {
  const chapter: LinkChapter = linkChapterDtoToLinkChapter(dto)
  return {
    type: LINKCHAPTER_LOAD,
    payload: chapter
  }
}

function linkChapterDtoToLinkChapter(dto: LinkChapterDto): LinkChapter {
  const licenceIds: string[] | undefined = dto.licences?.map(({ id }) => id)
  return {
    ...dto,
    id: dto.id,
    name: dto.name as string,
    thumbnailUrl: dto.thumbnailUrl as string,
    url: dto.url as string,
    order: dto.order,
    zone: dto.zone,
    licenceIds: licenceIds ? licenceIds : []
  }
}
